import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Player from '@vimeo/player';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import hamIcon from '../../assets/ham-icon.svg';
import { VIDEO_PROGRESS_SAVE_INTERVAL_MS, VIDEO_COMPLETION_THRESHOLD_PERCENT } from '../../constants/lessonProgress';
import { PopoverMenuComponent } from '../ui/PopoverMenuComponent';

import { Course } from '../../types/course';
import {
  VideoProgress,
  UserCourse,
  LessonType,
  getVideoProgress,
  updateVideoLesson,
  calculateCourseProgress,
} from '../../types/userCourse';
import { userCourseService } from '../../services/userCourseService';
import { showError } from '../../services/toastService';
import { useProfileStore } from '../../store/useProfileStore';
import { progressService } from '../../services/progressService';
import { badgeModalService } from '../../services/badgeModalService';

// ============================================================================
// COMPONENT
// ============================================================================

interface LessonVideoProps {
  course: Course;
  userCourse: UserCourse | null;
  onProgressUpdate?: (userCourse: UserCourse) => void;
  progressToNext?: () => void;
  onToggleSidebar?: () => void;
  hasNextLesson: boolean;
  hasPreviousLesson: boolean;
  onNavigateToNext?: () => void;
  onNavigateToPrevious?: () => void;
  stepNumber: number;
  totalSteps: number;
  nextStepLabel?: string;
}

export const LessonVideo: React.FC<LessonVideoProps> = ({
  course,
  userCourse,
  onProgressUpdate,
  progressToNext,
  onToggleSidebar,
  hasNextLesson,
  hasPreviousLesson,
  onNavigateToNext,
  onNavigateToPrevious,
  stepNumber,
  totalSteps,
  nextStepLabel
}) => {
  const history = useHistory();
  const playerRef = useRef<Player | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const saveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartRef = useRef<Date>(new Date());
  const currentProgressRef = useRef<VideoProgress | null>(null);
  const hasMarkedCompleteRef = useRef<boolean>(false);
  const hasCheckedBadgesRef = useRef<boolean>(false);

  const profile = useProfileStore((state) => state.activeProfile);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentProgress, setCurrentProgress] = useState<VideoProgress | null>(null);
  const [hasMarkedComplete, setHasMarkedComplete] = useState(false);
  const [showNavigationPopover, setShowNavigationPopover] = useState(false);
  const [navigationPopoverEvent, setNavigationPopoverEvent] = useState<Event | undefined>(undefined);
  const [navigationDirection, setNavigationDirection] = useState<'next' | 'previous' | null>(null);
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Find the video based on stepNumber (which corresponds to order)
  const currentVideo = course.videos?.find(v => v.order === stepNumber);
  const videoUrl = currentVideo?.url || course.videos?.[0]?.url;
  const videoTitle = currentVideo?.title || course.videos?.[0]?.title || 'Video';

  // Each video lesson has lessonId format: UUID with order number at the end
  // e.g., order 1 -> "00000000-0000-0000-0000-000000000001"
  // e.g., order 12 -> "00000000-0000-0000-0000-000000000012"
  const videoOrder = stepNumber;
  const videoLessonId = `00000000-0000-0000-0000-${videoOrder.toString().padStart(12, '0')}`;

  // Determine lesson status based on userCourse data
  const lessonStatus = React.useMemo(() => {
    if (!userCourse) return 'Not Started';

    const lesson = userCourse.userLessons.find(
      l => l.lessonId === videoLessonId && l.lessonType === LessonType.VIDEO
    );

    if (!lesson || !lesson.startedAt) return 'Not Started';
    if (lesson.completedAt) return 'Completed';
    return 'In Progress';
  }, [userCourse, videoLessonId]);

  // Get status styling
  const getStatusStyle = () => {
    switch (lessonStatus) {
      case 'Completed':
        return 'bg-green-500 text-white';
      case 'In Progress':
        return 'bg-red-500 text-white';
      case 'Not Started':
        return 'bg-medium';
      default:
        return 'bg-medium';
    }
  };

  // Handle navigation with confirmation if lesson is in progress
  const handleNavigationClick = (direction: 'next' | 'previous', event: React.MouseEvent) => {
    // If video is playing and going back, let history.block() handle it
    if (direction === 'previous' && isVideoPlaying) {
      onNavigateToPrevious?.();
      return;
    }

    if (lessonStatus === 'In Progress') {
      setNavigationDirection(direction);
      setNavigationPopoverEvent(event.nativeEvent);
      setShowNavigationPopover(true);
    } else {
      // If not in progress, navigate directly
      if (direction === 'next') {
        onNavigateToNext?.();
      } else {
        onNavigateToPrevious?.();
      }
    }
  };

  const handlePopoverDismiss = (selectedKey?: string) => {
    setShowNavigationPopover(false);
    setNavigationPopoverEvent(undefined);

    if (selectedKey === 'confirm') {
      // User confirmed navigation
      if (navigationDirection === 'next') {
        onNavigateToNext?.();
      } else if (navigationDirection === 'previous') {
        onNavigateToPrevious?.();
      }
    }
    setNavigationDirection(null);
  };

  // Validate Vimeo URL
  const isValidVimeoUrl = (url: string): boolean => {
    // Handle various Vimeo URL formats including privacy hash
    // https://vimeo.com/123456789
    // https://vimeo.com/123456789/ec6eccc897 (with privacy hash)
    // https://player.vimeo.com/video/123456789
    const patterns = [
      /vimeo\.com\/\d+/,
      /player\.vimeo\.com\/video\/\d+/,
    ];

    return patterns.some(pattern => pattern.test(url));
  };

  // Reset completion overlay/state when switching to a different video so we don't show "Next step" on the new video
  useEffect(() => {
    setShowCompletionOverlay(false);
    setHasMarkedComplete(false);
    hasMarkedCompleteRef.current = false;
  }, [stepNumber]);

  // Save progress to UserCourse
  // When overrideLessonId is set (e.g. from cleanup when switching videos), save to that lesson instead of current videoLessonId
  const saveProgress = async (progress: VideoProgress, overrideLessonId?: string) => {
    if (!userCourse) {
      showError('Cannot save video progress - user course data not loaded');
      return;
    }

    const targetLessonId = overrideLessonId ?? videoLessonId;

    // Find existing video lesson for this specific video
    const existingLesson = userCourse.userLessons.find(
      lesson => lesson.lessonId === targetLessonId && lesson.lessonType === LessonType.VIDEO
    );

    // Update lesson with new progress
    const updatedLesson = updateVideoLesson(
      existingLesson,
      progress,
      targetLessonId,
      VIDEO_COMPLETION_THRESHOLD_PERCENT,
      sessionStartRef.current
    );

    // Update UserCourse with modified lesson
    const updatedLessons = existingLesson
      ? userCourse.userLessons.map(l =>
          l.lessonId === targetLessonId && l.lessonType === LessonType.VIDEO
            ? updatedLesson
            : l
        )
      : [...userCourse.userLessons, updatedLesson];

    // Calculate overall course progress and lessons completed
    const { progress: courseProgress, lessonsCompleted } = calculateCourseProgress(
      updatedLessons,
      course.steps
    );

    const updatedUserCourse: UserCourse = {
      ...userCourse,
      userLessons: updatedLessons,
      progress: courseProgress,
      lessonsCompleted,
      lastAccessedAt: overrideLessonId ? userCourse.lastAccessedAt : new Date().toISOString(),
      lastAccessedLesson: LessonType.VIDEO,
      lastAccessedLessonId: overrideLessonId ? userCourse.lastAccessedLessonId : videoLessonId,
      lessonResponses: userCourse.lessonResponses || []
    };

    // Call service stub (will eventually persist to backend)
    await userCourseService.updateUserCourse(updatedUserCourse);

    // Notify parent component to update state.
    // Skip during cleanup saves (overrideLessonId set) — the component is switching lessons/courses,
    // so updating React state would contaminate the next course's userCourse in shared context.
    if (!overrideLessonId) {
      onProgressUpdate?.(updatedUserCourse);
    }

    // Check for newly earned badges if course is completed (100% progress)
    // Only check once per completion to avoid duplicate checks
    if (courseProgress >= 100 && !hasCheckedBadgesRef.current && profile?.id) {
      hasCheckedBadgesRef.current = true;
      // Brief delay so backend can persist progress and assign badge before we fetch summary
      // (video-only courses hit 100% in one step, so summary can be stale if we fetch immediately)
      const badgeCheckDelayMs = 1200;
      setTimeout(() => {
        progressService.getTeamUserSummary(profile.id)
          .then(summary => {
            if (summary.badges) {
              badgeModalService.checkAndShowNewBadges(summary.badges, profile.id);
            }
          })
          .catch(err => {
            console.error('Failed to check for badges after video completion:', err);
          });
      }, badgeCheckDelayMs);
    }
  };

  // Initialize Vimeo player
  useEffect(() => {
    if (!containerRef.current || !videoUrl) return;

    if (!isValidVimeoUrl(videoUrl)) {
      setError('Invalid Vimeo URL');
      setIsLoading(false);
      return;
    }

    const lessonIdForThisEffect = videoLessonId;
    let isMounted = true;

    // Clear ref so interval/cleanup don't save stale progress from a previous video to this lesson
    currentProgressRef.current = null;

    // Initialize player with URL instead of ID to support privacy hash
    const player = new Player(containerRef.current, {
      url: videoUrl as any,
      width: containerRef.current.offsetWidth,
      responsive: true,
    });

    playerRef.current = player;

    // Load saved progress and resume playback
    const initializeProgress = async () => {
      if (!isMounted) return;

      try {
        // Get video duration from player
        const duration = await player.getDuration();
        if (!isMounted) return;

        // Find video lesson in UserCourse for this specific video
        const videoLesson = userCourse?.userLessons.find(
          lesson => lesson.lessonId === videoLessonId && lesson.lessonType === LessonType.VIDEO
        );

        // Map Lesson to VideoProgress for component use
        const savedProgress = getVideoProgress(videoLesson, duration);

        if (savedProgress) {
          setCurrentProgress(savedProgress);
          setHasMarkedComplete(savedProgress.isComplete);
          hasMarkedCompleteRef.current = savedProgress.isComplete;
          currentProgressRef.current = savedProgress;

          // Resume from last position if not complete and position > 5 seconds
          if (!savedProgress.isComplete && savedProgress.currentPosition > 5) {
            await player.setCurrentTime(savedProgress.currentPosition);
            if (!isMounted) return;
          }
        } else {
          // Create new VideoProgress for first-time viewing
          const newProgress: VideoProgress = {
            videoId: course.videoId,
            currentPosition: 0,
            duration,
            percentComplete: 0,
            isComplete: false,
            lastWatched: new Date().toISOString(),
          };
          setCurrentProgress(newProgress);
          currentProgressRef.current = newProgress;
        }

        // Mark session start time
        sessionStartRef.current = new Date();

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Setup event listeners
    const setupEventListeners = () => {
      let hasStartedPlaying = false;

      // Track video playing state
      player.on('play', () => {
        setIsVideoPlaying(true);
      });

      // Track playback progress
      player.on('timeupdate', async (data) => {
        const { seconds, percent, duration } = data;
        const percentComplete = Math.round(percent * 100);
        const isComplete = percentComplete >= VIDEO_COMPLETION_THRESHOLD_PERCENT;

        const progress: VideoProgress = {
          videoId: course.videoId,
          currentPosition: seconds,
          duration,
          percentComplete: percentComplete,
          isComplete,
          lastWatched: new Date().toISOString(),
        };

        setCurrentProgress(progress);
        currentProgressRef.current = progress;

        // Save progress immediately when video first starts playing
        if (!hasStartedPlaying && seconds > 0) {
          hasStartedPlaying = true;
          await saveProgress(progress);
        }

        // Mark as complete when threshold is reached (only once)
        if (isComplete && !hasMarkedCompleteRef.current) {
          hasMarkedCompleteRef.current = true;
          setHasMarkedComplete(true);
          setShowCompletionOverlay(true);
          await saveProgress(progress);
          // Reset badge check flag when video completes
          hasCheckedBadgesRef.current = false;
        }
      });

      // Save progress when video ends
      player.on('ended', async () => {
        setIsVideoPlaying(false);
        if (currentProgress) {
          const completeProgress: VideoProgress = {
            ...currentProgress,
            percentComplete: 100,
            isComplete: true,
            lastWatched: new Date().toISOString(),
          };

          await saveProgress(completeProgress);

          if (!hasMarkedCompleteRef.current) {
            hasMarkedCompleteRef.current = true;
            setHasMarkedComplete(true);
            setShowCompletionOverlay(true);
            // Reset badge check flag when video ends
            hasCheckedBadgesRef.current = false;
          }
        }
      });

      // Save progress when user pauses
      player.on('pause', async () => {
        setIsVideoPlaying(false);
        if (currentProgress) {
          await saveProgress(currentProgress);
        }
      });

      // Handle errors
      player.on('error', (error) => {
        setError('Error loading video');
      });
    };

    // Initialize
    player.ready()
      .then(() => {
        if (!isMounted || playerRef.current !== player) return;
        setupEventListeners();
        initializeProgress();
      })
      .catch(() => {
        if (isMounted) setIsLoading(false);
      });

    // Auto-save progress during playback
    saveIntervalRef.current = setInterval(async () => {
      if (currentProgressRef.current && !hasMarkedCompleteRef.current) {
        await saveProgress(currentProgressRef.current);
      }
    }, VIDEO_PROGRESS_SAVE_INTERVAL_MS);

    // Cleanup: save progress for the video we're leaving using its lesson id (not the current video's id after re-render)
    return () => {
      isMounted = false;

      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }

      const progressToSave = currentProgressRef.current;
      if (progressToSave) {
        saveProgress(progressToSave, lessonIdForThisEffect).catch(() => {});
      }

      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (err) {
          console.debug('Player cleanup error (safe to ignore):', err);
        }
        playerRef.current = null;
      }

      hasCheckedBadgesRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, course.videoId, stepNumber]);


  // Block navigation when video is actively playing
  useEffect(() => {
    if (!isVideoPlaying) return;

    const unblock = history.block(() => {
      // Pause the video when user is prompted
      if (playerRef.current) {
        playerRef.current.pause().catch(() => {});
      }
      setIsVideoPlaying(false);
      return 'Are you sure you want to leave? The video is currently playing.';
    });

    return () => {
      unblock();
    };
  }, [isVideoPlaying, history]);

  // Test panel: "Go To End" — seek video to 95% so tester only waits a few seconds
  useEffect(() => {
    const handleGoToEnd = async () => {
      if (!playerRef.current) return;
      try {
        const duration = await playerRef.current.getDuration();
        await playerRef.current.setCurrentTime(duration * 0.98);
        await playerRef.current.play();
      } catch (e) {
        // Player may not be ready yet
      }
    };

    window.addEventListener('test-panel-go-to-end', handleGoToEnd);
    return () => window.removeEventListener('test-panel-go-to-end', handleGoToEnd);
  }, []);

  return (
    <div className="pt-4 px-4 sm:px-8 pb-8" data-tour="welcome-course-video">
      {/* Video Title with Hamburger Menu */}
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
          aria-label="Toggle menu"
        >
          <IonIcon icon={hamIcon} size="small" color="medium" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold">{videoTitle}</h1>
      </div>

      {/* Lesson Progress Indicator */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
        </div>
        <div className="flex items-center">
          <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
            {lessonStatus}
          </div>
          <div className="flex gap-0.5">
            <IonButton
              className="toolbar-button-icon-small"
              fill="solid"
              size="small"
              color="medium"
              onClick={(e) => handleNavigationClick('previous', e)}
            >
              <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
            </IonButton>
            <IonButton
              className="toolbar-button-icon-small"
              fill="solid"
              size="small"
              color="medium"
              disabled={!hasNextLesson}
              onClick={(e) => handleNavigationClick('next', e)}
            >
              <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
            </IonButton>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      {/* Progress Indicator: Removed, Lesson type Video uses vimeo progress indicator */}

      {/* Video Player Container */}
      <div className="relative bg-black rounded-lg overflow-hidden" style={{ paddingTop: '56.25%' }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <IonSpinner name="crescent" color="light" />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
            <div className="text-center">
              <p className="text-lg font-semibold mb-2">⚠️ Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div
          ref={containerRef}
          className="absolute inset-0"
          style={{ width: '100%', height: '100%' }}
        />

        {/* Completion Overlay */}
        {showCompletionOverlay && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-secondary rounded-lg shadow-lg p-4">
              <IonButton color="white" size="large" fill="clear" onClick={progressToNext}>
                {nextStepLabel || 'Next Step'}
                <IonIcon slot="end" icon={chevronForwardOutline} />
              </IonButton>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Confirmation Popover */}
      <PopoverMenuComponent
        isOpen={showNavigationPopover}
        event={navigationPopoverEvent}
        onDismiss={handlePopoverDismiss}
        menuItems={[
          {
            key: 'confirm',
            value: `Click to confirm you want to leave and go to the ${navigationDirection === 'next' ? 'next' : 'previous'} step.`
          }
        ]}
      />
    </div>
  );
};
